import type React from 'react';
import {Box, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import type {ExternalSignals} from '../../types/signalsTypes';
import {ProfileSection} from '../components/ProfileSection';

const COLOR = {High: 'error', Medium: 'warning', Low: 'success'} as const;

/** External category risk (spec 4.12): score, label and change vs last month (external only), impact by input type. */
export const ExternalRiskCard: React.FC<{risk: ExternalSignals['risk']}> = ({risk}) => (
    <ProfileSection title="External category risk" subtitle="From external signals only">
        {risk.score === null ? (
            <Typography variant="body2" color="text.secondary">No signals in the last 90 days, so no external risk score.</Typography>
        ) : (
            <Stack gap={2}>
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                    <Box sx={{textAlign: 'center', minWidth: 72}}>
                        <Typography variant="h4" sx={{fontWeight: 600, lineHeight: 1}}>{risk.score}</Typography>
                        <Typography variant="caption" color="text.secondary">of 100</Typography>
                    </Box>
                    {risk.label && <Tag label={`${risk.label} risk`} color={COLOR[risk.label]}/>}
                    {risk.delta !== null && (
                        <Typography variant="body2" color={risk.delta > 0 ? 'error.main' : risk.delta < 0 ? 'success.main' : 'text.secondary'}>
                            {`${risk.delta > 0 ? '+' : ''}${risk.delta} points vs last month`}
                        </Typography>
                    )}
                    {risk.headline && <Typography variant="body2" sx={{flex: 1, minWidth: 200}}>{risk.headline}</Typography>}
                </Stack>
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small" aria-label="Impact by input type">
                        <TableHead>
                            <TableRow>
                                <TableCell>Input type</TableCell>
                                <TableCell sx={{width: '30%'}}>Share of exposure</TableCell>
                                <TableCell align="right">Events</TableCell>
                                <TableCell>Latest event</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {risk.byInput.map(r => (
                                <TableRow key={r.type}>
                                    <TableCell>{r.label}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <LinearProgress variant="determinate" value={r.sharePct} sx={{flex: 1, height: 6, borderRadius: 3}}
                                                            aria-label={`${r.label} share of exposure`}/>
                                            <Typography variant="caption" sx={{width: 44, textAlign: 'right'}}>{`${r.sharePct.toFixed(0)}%`}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">{r.events}</TableCell>
                                    <TableCell>{r.latest}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Score: signals of the last 90 days weighted by impact (high 3, medium 2, low 1) and relevance; 67+ is high, 34+ medium.
                </Typography>
            </Stack>
        )}
    </ProfileSection>
);
